import { t } from "../contract/strings";

/**
 * The one finalise response the browser may retry.
 *
 * The API client keeps the parsed response on `error.data`. The server's HTTP error
 * envelope puts the code under `detail`, while a few test and proxy boundaries expose the
 * already-unwrapped `error` member. Both forms are the same wire value; no other status or
 * error is retryable here.
 */
export const FINAL_WATERMARK_PENDING = "final_watermark_pending" as const;

/**
 * The bounded finalise retry policy: 500, 1000, 2000 ms, then every 4000 ms, for as long
 * as the backoff fits in `FINALISE_RETRY_DEADLINE_MS`.
 *
 * The bound outlasts another device's drain (#925). A device that still owes audio at
 * Stop holds its final watermark for up to `STREAM_BACKLOG_UPLOAD_WINDOW_MS` (90 s), and
 * the server refuses everyone else's report with `final_watermark_pending` until it lands.
 * 120 s is that window plus a margin; past it the meeting really is stuck and the person
 * is told. The core's `FinaliseRetry` gives up after the same attempt count.
 */
export const FINALISE_RETRY_DEADLINE_MS = 120_000;
export const FINALISE_RETRY_INITIAL_MS = 500;
export const FINALISE_RETRY_FACTOR = 2;
export const FINALISE_RETRY_MAX_DELAY_MS = 4_000;

function finaliseRetryBackoffMs(attempts: number): number {
  return Math.min(
    FINALISE_RETRY_INITIAL_MS * FINALISE_RETRY_FACTOR ** (attempts - 1),
    FINALISE_RETRY_MAX_DELAY_MS,
  );
}

/** Attempts whose backoffs fit in the deadline: 33, with 119.5 s of backoff. */
export const FINALISE_RETRY_MAX_ATTEMPTS = (() => {
  let attempts = 1;
  let spent = 0;
  while (spent + finaliseRetryBackoffMs(attempts) <= FINALISE_RETRY_DEADLINE_MS) {
    spent += finaliseRetryBackoffMs(attempts);
    attempts += 1;
  }
  return attempts;
})();

export const FINALISE_RETRYING_MESSAGE = t("finalise.retrying");
export const FINALISE_RETRY_EXHAUSTED_MESSAGE = t("finalise.exhausted");

export interface FinaliseRetryTimer {
  setTimeout(callback: () => void, delayMs: number): unknown;
  clearTimeout(handle: unknown): void;
  now(): number;
}

/** The browser timer ports keep the retry state machine deterministic in tests. */
export const FINALISE_RETRY_TIMER: FinaliseRetryTimer = {
  setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
  clearTimeout: (handle) =>
    globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
  now: () => Date.now(),
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Match only the typed final-watermark-pending response.
 *
 * In particular, a bare 409, a different detail code, and a transport/application error
 * must pass through to the caller without a retry. Keeping this predicate separate makes
 * that negative contract visible to both the controller and its tests.
 */
export function isFinalWatermarkPendingError(error: unknown): boolean {
  if (!isRecord(error) || error.status !== 409 || !isRecord(error.data))
    return false;
  const data = isRecord(error.data.detail) ? error.data.detail : error.data;
  return data.error === FINAL_WATERMARK_PENDING;
}

export class FinaliseRetryExhaustedError extends Error {
  readonly reason = "finalise_retry_exhausted" as const;

  constructor(cause: unknown) {
    super(FINALISE_RETRY_EXHAUSTED_MESSAGE);
    this.name = "FinaliseRetryExhaustedError";
    this.cause = cause;
  }

  readonly cause: unknown;
}

export class FinaliseRetryAbortedError extends Error {
  readonly reason = "finalise_retry_aborted" as const;

  constructor() {
    super("finalise retry aborted");
    this.name = "FinaliseRetryAbortedError";
  }
}

interface RetryOperation<T> {
  attempts: number;
  startedAt: number;
  inFlight: boolean;
  cancelled: boolean;
  settled: boolean;
  timer: unknown;
  deadlineTimer: unknown;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
  promise: Promise<T>;
  abortSignal?: AbortSignal;
  abortListener?: () => void;
}

export interface FinaliseRetryOptions {
  timer?: FinaliseRetryTimer;
  signal?: AbortSignal;
  /**
   * Called once per typed pending-watermark refusal, before the next attempt is
   * scheduled. `attempt` is how many attempts have now been made, so the first
   * 409 reports 1. Optional argument: existing callers that only update the
   * status line ignore it.
   */
  onRetrying?: (attempt: number) => void;
  onExhausted?: (error: FinaliseRetryExhaustedError) => void;
}

/**
 * A single-flight, bounded retry controller for finalise/leave.
 *
 * `run()` returns the same promise while an attempt or backoff is active, so duplicate Stop
 * taps cannot create a second request. `cancel()` clears both timers and suppresses all
 * continuations; an API function that does not accept an AbortSignal can still finish its
 * already-started request, but it cannot trigger another retry or update the caller.
 */
export class FinaliseRetryController<T> {
  private active: RetryOperation<T> | null = null;

  constructor(
    private readonly request: () => Promise<T>,
    private readonly options: FinaliseRetryOptions = {},
  ) {}

  /** Start a fresh bounded run, or return the active run for a duplicate caller. */
  run(): Promise<T> {
    if (this.active) return this.active.promise;

    const timer = this.options.timer ?? FINALISE_RETRY_TIMER;
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const operation: RetryOperation<T> = {
      attempts: 0,
      startedAt: timer.now(),
      inFlight: false,
      cancelled: false,
      settled: false,
      timer: null,
      deadlineTimer: null,
      resolve,
      reject,
      promise,
    };
    this.active = operation;

    const signal = this.options.signal;
    operation.abortSignal = signal;
    operation.abortListener = () => this.cancel();
    if (signal?.aborted) {
      this.cancel();
    } else {
      signal?.addEventListener("abort", operation.abortListener, {
        once: true,
      });
      // The deadline also bounds a request that never produces an HTTP response. The
      // underlying fetch is intentionally not replaced or retried: once this fires the
      // caller receives a typed terminal result and no later continuation can publish.
      operation.deadlineTimer = timer.setTimeout(() => {
        if (!operation.settled && !operation.cancelled) this.exhaust(operation);
      }, FINALISE_RETRY_DEADLINE_MS);
      this.attempt(operation);
    }
    return promise;
  }

  /** Cancel the active retry and make an abort/unmount a non-error terminal path. */
  cancel(): void {
    const operation = this.active;
    if (!operation || operation.settled || operation.cancelled) return;
    operation.cancelled = true;
    this.clearTimers(operation);
    this.removeAbortListener(operation);
    operation.reject(new FinaliseRetryAbortedError());
    operation.settled = true;
    if (!operation.inFlight) this.active = null;
  }

  get inFlight(): boolean {
    return this.active !== null;
  }

  private attempt(operation: RetryOperation<T>): void {
    if (operation.cancelled || operation.settled) return;
    const timer = this.options.timer ?? FINALISE_RETRY_TIMER;
    if (timer.now() - operation.startedAt >= FINALISE_RETRY_DEADLINE_MS) {
      this.exhaust(operation);
      return;
    }

    operation.attempts += 1;
    operation.inFlight = true;
    Promise.resolve()
      .then(() => this.request())
      .then(
        (value) => {
          operation.inFlight = false;
          if (operation.cancelled || operation.settled) {
            this.releaseIfFinished(operation);
            return;
          }
          this.clearTimers(operation);
          this.removeAbortListener(operation);
          operation.settled = true;
          operation.resolve(value);
          this.active = null;
        },
        (error: unknown) => {
          operation.inFlight = false;
          if (operation.cancelled || operation.settled) {
            this.releaseIfFinished(operation);
            return;
          }
          this.failed(operation, error);
        },
      );
  }

  private failed(operation: RetryOperation<T>, error: unknown): void {
    const timer = this.options.timer ?? FINALISE_RETRY_TIMER;
    if (!isFinalWatermarkPendingError(error)) {
      this.clearTimers(operation);
      this.removeAbortListener(operation);
      operation.settled = true;
      operation.reject(error);
      this.active = null;
      return;
    }

    this.options.onRetrying?.(operation.attempts);
    const elapsed = timer.now() - operation.startedAt;
    if (
      operation.attempts >= FINALISE_RETRY_MAX_ATTEMPTS ||
      elapsed >= FINALISE_RETRY_DEADLINE_MS
    ) {
      this.exhaust(operation);
      return;
    }

    const requestedDelay = finaliseRetryBackoffMs(operation.attempts);
    const remaining = FINALISE_RETRY_DEADLINE_MS - elapsed;
    operation.timer = timer.setTimeout(
      () => {
        operation.timer = null;
        if (operation.cancelled || operation.settled) return;
        if (timer.now() - operation.startedAt >= FINALISE_RETRY_DEADLINE_MS) {
          this.exhaust(operation);
          return;
        }
        this.attempt(operation);
      },
      Math.min(requestedDelay, remaining),
    );
  }

  private exhaust(operation: RetryOperation<T>): void {
    if (operation.cancelled || operation.settled) return;
    this.clearTimers(operation);
    this.removeAbortListener(operation);
    const error = new FinaliseRetryExhaustedError(FINAL_WATERMARK_PENDING);
    operation.settled = true;
    this.options.onExhausted?.(error);
    operation.reject(error);
    if (!operation.inFlight) this.active = null;
  }

  private clearTimers(operation: RetryOperation<T>): void {
    const timer = this.options.timer ?? FINALISE_RETRY_TIMER;
    if (operation.timer !== null) {
      timer.clearTimeout(operation.timer);
      operation.timer = null;
    }
    if (operation.deadlineTimer !== null) {
      timer.clearTimeout(operation.deadlineTimer);
      operation.deadlineTimer = null;
    }
  }

  private removeAbortListener(operation: RetryOperation<T>): void {
    if (operation.abortSignal && operation.abortListener) {
      operation.abortSignal.removeEventListener(
        "abort",
        operation.abortListener,
      );
      operation.abortListener = undefined;
    }
  }

  private releaseIfFinished(operation: RetryOperation<T>): void {
    if (operation.settled && this.active === operation) this.active = null;
  }
}
